import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import SelectWithAdd from '@/components/SelectWithAdd';
import PaginationBar, { usePagedRows } from '@/components/PaginationBar';
import { toast } from 'sonner';
import { ArrowLeft, Search, Edit, Trash2, UserSearch, Plus, Phone, Eye } from 'lucide-react';

const API = `${(window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "")}/api`;

const STATUSES = [
  { value: 'interesante', label: 'Interesante', cls: 'bg-violet-100 text-violet-800 border-violet-300' },
  { value: 'contactado', label: 'Contactado', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { value: 'descartado', label: 'Descartado', cls: 'bg-gray-100 text-gray-600 border-gray-300' },
];
const statusInfo = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];

const formatDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ageFrom = (birth) => {
  if (!birth) return null;
  const b = new Date(birth + 'T12:00:00');
  if (isNaN(b)) return null;
  const t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a -= 1;
  return a;
};

const EMPTY = {
  first_name: '', last_name: '', birth_date: '', club: '', position: '',
  contact_name: '', contact_phone: '', notes: '',
};

const Prospects = () => {
  usePageTitle('Jugadores de interés');
  const navigate = useNavigate();
  const { user, isSuperAdmin, isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [editing, setEditing] = useState(null); // null | 'new' | prospect
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const canModify = (p) => isSuperAdmin || isAdmin || p.created_by === user?.id;

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/prospects`);
      setRows(res.data || []);
    } catch (e) {
      toast.error('Error al cargar los jugadores de interés');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((p) => {
      if (statusFilter !== 'all' && (p.status || 'interesante') !== statusFilter) return false;
      if (!term) return true;
      return `${p.first_name} ${p.last_name} ${p.club || ''}`.toLowerCase().includes(term);
    });
  }, [rows, search, statusFilter]);

  const { paged, pagination } = usePagedRows(filtered, { initialPageSize: 25 });

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      first_name: p.first_name || '', last_name: p.last_name || '', birth_date: p.birth_date || '',
      club: p.club || '', position: p.position || '', contact_name: p.contact_name || '',
      contact_phone: p.contact_phone || '', notes: p.notes || '',
    });
  };
  const openNew = () => { setEditing('new'); setForm(EMPTY); };

  const handleSave = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      return toast.error('Nombre y apellido son obligatorios');
    }
    try {
      setSaving(true);
      if (editing === 'new') {
        const payload = {};
        Object.entries(form).forEach(([k, v]) => { if (v.trim()) payload[k] = v.trim(); });
        await axios.post(`${API}/prospects`, payload);
        toast.success('Jugador de interés creado');
      } else {
        await axios.put(`${API}/prospects/${editing.id}`, form);
        toast.success('Cambios guardados');
      }
      setEditing(null);
      load();
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (p, status) => {
    try {
      await axios.put(`${API}/prospects/${p.id}`, { status });
      setRows((prev) => prev.map((r) => (r.id === p.id ? { ...r, status } : r)));
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'No se pudo cambiar el estado');
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/prospects/${toDelete.id}`);
      toast.success('Jugador eliminado');
      setToDelete(null);
      load();
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al eliminar');
      setToDelete(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-4 max-w-4xl mx-auto" data-testid="prospects-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate('/seguimiento')} data-testid="btn-back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <UserSearch className="w-7 h-7 text-violet-600" />
                Jugadores de interés
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Jugadores que los scouts vieron y que no representa la agencia.
              </p>
            </div>
          </div>
          <Button onClick={openNew} className="bg-brand-600 hover:bg-brand-700" data-testid="btn-new-prospect">
            <Plus className="w-4 h-4 mr-2" /> Nuevo
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar por nombre o club..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="prospects-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-52" data-testid="prospects-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600"></div>
          </div>
        ) : filtered.length === 0 ? (
          <Card className="p-10 text-center">
            <UserSearch className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No hay jugadores de interés</p>
            <p className="text-sm text-gray-400 mt-1">
              Se agregan desde un seguimiento, con "Agregar jugador no registrado".
            </p>
          </Card>
        ) : (
          <>
            <div className="space-y-3">
              {paged.map((p) => {
                const st = statusInfo(p.status);
                const age = ageFrom(p.birth_date);
                return (
                  <Card key={p.id} className="p-4" data-testid={`prospect-${p.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {p.first_name} {p.last_name}
                          {age != null && <span className="text-gray-500 font-normal"> · {age} años</span>}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {p.position || 'Sin posición'}{p.club ? ` · ${p.club}` : ''}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-600 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {p.visits || 0} {p.visits === 1 ? 'visita' : 'visitas'}
                            {p.last_visit ? ` (última: ${formatDate(p.last_visit)})` : ''}
                          </span>
                          {p.contact_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {p.contact_name ? `${p.contact_name}: ` : ''}{p.contact_phone}
                            </span>
                          )}
                          {p.created_by_name && <span className="text-gray-400">Cargado por {p.created_by_name}</span>}
                        </div>
                        {p.notes && <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{p.notes}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        {canModify(p) ? (
                          <Select value={p.status || 'interesante'} onValueChange={(v) => changeStatus(p, v)}>
                            <SelectTrigger className={`h-8 w-36 text-xs border ${st.cls}`} data-testid={`status-${p.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge className={`border ${st.cls}`}>{st.label}</Badge>
                        )}
                        {canModify(p) && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => setToDelete(p)} data-testid={`delete-${p.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
            <PaginationBar {...pagination} />
          </>
        )}
      </div>

      {/* Crear / editar */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Nuevo jugador de interés' : 'Editar jugador de interés'}</DialogTitle>
            <DialogDescription>Solo nombre y apellido son obligatorios.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nombres *</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1" data-testid="form-first-name" />
              </div>
              <div>
                <Label className="text-xs">Apellidos *</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1" data-testid="form-last-name" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Fecha de nacimiento</Label>
              <Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Club donde juega</Label>
              <SelectWithAdd catalogType="teams" value={form.club} onChange={(v) => setForm({ ...form, club: v })} placeholder="Elegir equipo..." testId="form-club" addLabel="Agregar equipo" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Posición</Label>
              <SelectWithAdd catalogType="positions" value={form.position} onChange={(v) => setForm({ ...form, position: v })} placeholder="Elegir posición..." testId="form-position" addLabel="Agregar posición" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Contacto</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Teléfono</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notas</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-brand-600 hover:bg-brand-700" data-testid="form-save">
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Eliminar */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar a {toDelete?.first_name} {toDelete?.last_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Solo se puede eliminar si no aparece en ningún seguimiento. Si ya fue observado, marcalo como "descartado".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default Prospects;
